/**
 * El resumen de campaña en pestañas (rediseño, Fase 1), sin React: qué pestaña y qué filtro dice la
 * URL, cómo se filtran personas y citas, qué avisos salen en «Requiere atención» y qué acciones se
 * enseñan a quién. Se prueba con Jest (resumenPestanas.test.js).
 *
 * Los permisos de aquí son solo para NO ENSEÑAR lo que no se puede hacer; la barrera es el servidor.
 */
import { esCitaAbierta, esCitaCancelada } from '../compartidos/etiquetasEstado';
import { ahoraEnMadrid, citaYaTermino } from '../compartidos/horaMadrid';

// ── Pestaña y filtro en la URL ───────────────────────────────────────────────────────────────
export const PESTANAS = ['resumen', 'personas', 'citas', 'tecnicos'];

export const FILTROS_PERSONAS = [
  { id: 'todos', label: 'Todos' },
  { id: 'sin_cita', label: 'Sin cita' },
  { id: 'reservada', label: 'Cita reservada' },
  { id: 'confirmada', label: 'Cita confirmada' },
  { id: 'completados', label: 'Completados' },
  { id: 'con_pendientes', label: 'Con subtareas pendientes' },
  { id: 'cancelados', label: 'Cancelados' },
];

export const FILTROS_CITAS = [
  { id: 'todas', label: 'Todas' },
  { id: 'abiertas', label: 'Abiertas' },
  { id: 'sin_cerrar', label: 'Sin cerrar' },
  { id: 'reservada', label: 'Reservadas' },
  { id: 'confirmada', label: 'Confirmadas' },
  { id: 'realizadas', label: 'Realizadas' },
  { id: 'no_asistio', label: 'No asistió' },
  { id: 'canceladas', label: 'Canceladas' },
];

const FILTRO_POR_DEFECTO = { personas: 'todos', citas: 'todas' };
const VALIDOS = {
  personas: FILTROS_PERSONAS.map((f) => f.id),
  citas: FILTROS_CITAS.map((f) => f.id),
};

/**
 * `?tab=` y `?filtro=` de la URL. Sin `tab`, o con uno que no existe, se abre «Resumen». El filtro solo
 * vale en la pestaña que lo tiene y si es uno de los suyos; si no, el de por defecto.
 * `params`: un URLSearchParams (o cualquier cosa con `.get`).
 */
export function leerPestana(params) {
  const t = params?.get?.('tab');
  const tab = PESTANAS.includes(t) ? t : 'resumen';
  const f = params?.get?.('filtro');
  const filtro = VALIDOS[tab] ? (VALIDOS[tab].includes(f) ? f : FILTRO_POR_DEFECTO[tab]) : null;
  return { tab, filtro };
}

/** Los parámetros para ir a una pestaña (con su filtro), conservando los demás de la URL. */
export function paramsDePestana(params, tab, filtro) {
  const n = new URLSearchParams(params ? params.toString() : '');
  if (tab === 'resumen') n.delete('tab'); else n.set('tab', tab);
  if (filtro && VALIDOS[tab] && filtro !== FILTRO_POR_DEFECTO[tab]) n.set('filtro', filtro); else n.delete('filtro');
  return n;
}

// ── Personas ────────────────────────────────────────────────────────────────────────────────
const CANCELADO = ['cancelado_usuario', 'cancelado_sistemas'];

/** En qué filtro (además de «todos») cae una persona. Una sola categoría por persona. */
export function categoriaPersona(u) {
  if (u.estado === 'pendiente_reserva') return 'sin_cita';
  if (u.estado === 'cita_reservada' && u.cita?.estado === 'reservada') return 'reservada';
  if (u.estado === 'cita_reservada' && u.cita?.estado === 'confirmada') return 'confirmada';
  if (u.estado === 'realizado') return u.tiene_subtareas_pendientes ? 'con_pendientes' : 'completados';
  if (CANCELADO.includes(u.estado)) return 'cancelados';
  return null;
}

export function contarPersonas(usuarios = []) {
  const c = Object.fromEntries(FILTROS_PERSONAS.map((f) => [f.id, 0]));
  for (const u of usuarios) {
    c.todos += 1;
    const k = categoriaPersona(u);
    if (k) c[k] += 1;
  }
  return c;
}

export function filtrarPersonas(usuarios = [], filtro = 'todos') {
  if (filtro === 'todos') return usuarios;
  return usuarios.filter((u) => categoriaPersona(u) === filtro);
}

// ── Citas ───────────────────────────────────────────────────────────────────────────────────
/** Abierta (reservada o confirmada) y ya debería haber terminado: día anterior o fin pasado (Madrid). */
export function citaSinCerrar(c, ahora = new Date()) {
  if (!esCitaAbierta(c.estado)) return false;
  const { hoy } = ahoraEnMadrid(ahora);
  return c.fecha < hoy || citaYaTermino(c, ahora);
}

function cumpleEstado(c, estado, ahora) {
  switch (estado) {
    case 'abiertas': return esCitaAbierta(c.estado);
    case 'sin_cerrar': return citaSinCerrar(c, ahora);
    case 'reservada': return c.estado === 'reservada';
    case 'confirmada': return c.estado === 'confirmada';
    case 'realizadas': return c.estado === 'realizada';
    case 'no_asistio': return c.estado === 'no_asistio';
    case 'canceladas': return esCitaCancelada(c.estado);
    default: return true;
  }
}

export function filtrarCitas(citas = [], { estado = 'todas', tecnico = '' } = {}, ahora = new Date()) {
  return citas.filter((c) => cumpleEstado(c, estado, ahora)
    && (!tecnico || String(c.tecnico?.id) === String(tecnico)));
}

export function contarCitas(citas = [], ahora = new Date()) {
  return Object.fromEntries(FILTROS_CITAS.map((f) => [f.id, citas.filter((c) => cumpleEstado(c, f.id, ahora)).length]));
}

// ── «Requiere atención» ─────────────────────────────────────────────────────────────────────
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

/**
 * Los avisos accionables, en orden de urgencia. Cada uno dice a qué pestaña y filtro lleva. Salen de
 * lo que ya llega (resumen y lotes), respetando los filtros de arriba: son las cifras de lo que se ve.
 *   · citas pasadas sin cerrar (el técnico no las cerró);
 *   · personas con la intervención hecha pero subtareas pendientes;
 *   · lotes sin avisar con la campaña en curso (en Pausada se avisan al reanudar);
 *   · personas aún sin cita, mientras la campaña está en curso.
 * `lotes` null = aún no cargados: ese aviso no sale (no se afirma «ninguno» sin haberlo mirado).
 */
export function avisosResumen({ campana, usuarios = [], citas = [], lotes = null }, ahora = new Date()) {
  const avisos = [];
  const enCurso = ['Activa', 'Pausada'].includes(campana?.estado);
  const sinCerrar = citas.filter((c) => citaSinCerrar(c, ahora)).length;
  if (sinCerrar) {
    avisos.push({ clave: 'sin_cerrar', tono: 'expired', texto: `${plural(sinCerrar, 'cita pasada', 'citas pasadas')} sin cerrar`, tab: 'citas', filtro: 'sin_cerrar', accion: 'Ver en Citas' });
  }
  const pendientes = usuarios.filter((u) => categoriaPersona(u) === 'con_pendientes').length;
  if (pendientes) {
    avisos.push({ clave: 'con_pendientes', tono: 'waiting', texto: `${plural(pendientes, 'persona', 'personas')} con subtareas pendientes`, tab: 'personas', filtro: 'con_pendientes', accion: 'Ver en Personas' });
  }
  if (enCurso && Array.isArray(lotes)) {
    const sinAvisar = lotes.filter((l) => !l.notificado_en);
    if (sinAvisar.length) {
      const nombres = sinAvisar.map((l) => `Lote ${l.numero}`).join(', ');
      avisos.push({
        clave: 'lotes_sin_avisar',
        tono: campana.estado === 'Pausada' ? 'info' : 'waiting',
        texto: campana.estado === 'Pausada'
          ? `${plural(sinAvisar.length, 'lote se avisará', 'lotes se avisarán')} al reanudar (${nombres})`
          : `${plural(sinAvisar.length, 'lote', 'lotes')} sin avisar (${nombres})`,
        tab: 'personas', filtro: null, accion: 'Ver lotes',
      });
    }
  }
  const sinCita = usuarios.filter((u) => categoriaPersona(u) === 'sin_cita').length;
  if (enCurso && sinCita) {
    avisos.push({ clave: 'sin_cita', tono: 'info', texto: `${plural(sinCita, 'persona', 'personas')} aún sin cita`, tab: 'personas', filtro: 'sin_cita', accion: 'Ver en Personas' });
  }
  return avisos;
}

// ── Qué se enseña a quién ───────────────────────────────────────────────────────────────────
/**
 * Super Administrador: todo. Eliminar: intervenciones.campanas.delete (la ruta DELETE). El resto de
 * gestión: intervenciones.campanas.manage. Exportar el CSV y ver: con entrar a la pantalla basta
 * (intervenciones.campanas.view, que exige la ruta). Cancelar y reabrir citas también las puede su
 * técnico, pero eso es desde su panel: aquí, gestión.
 */
export function permisosResumen({ rol, tienePermiso, estado }) {
  const sa = rol === 'Super Administrador';
  const tiene = (p) => sa || (typeof tienePermiso === 'function' && !!tienePermiso(p));
  const gestionar = tiene('intervenciones.campanas.manage');
  const borrar = tiene('intervenciones.campanas.delete');
  const enCurso = ['Activa', 'Pausada'].includes(estado);
  return {
    gestionar,
    editar: gestionar && ['Borrador', 'Activa', 'Pausada'].includes(estado),
    reactivar: gestionar && estado === 'Cancelada',
    eliminar: borrar && estado === 'Cancelada',
    altaDeFuera: gestionar && enCurso,
    registrar: gestionar,
    cancelarCita: gestionar,
    reabrirCita: gestionar,
    exportar: true,
  };
}
